import { getCatalogRamSnapshotCache } from '../../catalog/infra/catalogRamSnapshotCache'
import type { WebCatalogPromotionRow, WebCatalogPromotionTargetRow } from '../../catalog/infra/catalogRamSnapshotCache';
import type { PromotionWithTargets } from '../domain/promotionTypes';
import {
  mapPromotionRow,
  mapTargetRow,
  type PromotionRow,
  type PromotionTargetRow,
} from './promotionsRepoShared';

function serializeMaybeJson(raw: unknown): string | null {
  if (raw == null) return null;
  if (typeof raw === 'string') return raw;
  try {
    return JSON.stringify(raw);
  } catch {
    return null;
  }
}

function remotePromotionToRow(p: WebCatalogPromotionRow, organizationId: string): PromotionRow | null {
  if (!p?.id) return null;
  if (p.deleted_at) return null;
  const orgId =
    p.organization_id != null && String(p.organization_id).trim() !== ''
      ? String(p.organization_id)
      : organizationId;
  return {
    id: String(p.id),
    organization_id: orgId,
    name: String(p.name ?? ''),
    description: p.description != null ? String(p.description) : null,
    kind: String(p.kind ?? 'percent'),
    scope: String(p.scope ?? 'cart'),
    value_percent: p.value_percent ?? null,
    value_fixed: p.value_fixed ?? null,
    combo_buy_qty: p.combo_buy_qty ?? null,
    combo_pay_qty: p.combo_pay_qty ?? null,
    combo_bundle_price: p.combo_bundle_price ?? null,
    starts_at: p.starts_at ?? null,
    ends_at: p.ends_at ?? null,
    is_active: p.is_active !== false,
    branch_ids: serializeMaybeJson(p.branch_ids) ?? (typeof p.branch_ids === 'string' ? p.branch_ids : null),
    apply_mode: String(p.apply_mode ?? 'auto'),
    promo_code: p.promo_code ?? null,
    priority: p.priority ?? 0,
    stackable: p.stackable === true,
    channels: serializeMaybeJson(p.channels) ?? (typeof p.channels === 'string' ? p.channels : null),
    conditions_json: serializeMaybeJson(p.conditions_json),
    created_at: p.created_at ?? null,
    updated_at: p.updated_at ?? null,
    deleted_at: p.deleted_at ?? null,
  };
}

function remoteTargetToRow(t: WebCatalogPromotionTargetRow): PromotionTargetRow | null {
  if (!t?.id || !t.promotion_id || !t.target_id) return null;
  return {
    id: String(t.id),
    promotion_id: String(t.promotion_id),
    target_type: String(t.target_type ?? 'product'),
    target_id: String(t.target_id),
    created_at: t.created_at ?? null,
  };
}

function listPromotionsFromWebCache(options?: {
  includeInactive?: boolean;
}): PromotionWithTargets[] | null {
  const cache = getCatalogRamSnapshotCache();
  if (!cache?.promotions?.length) return null;

  const orgId = cache.promotions.find((p) => p.organization_id)?.organization_id ?? '';
  const rows = cache.promotions
    .map((p) => remotePromotionToRow(p, String(orgId)))
    .filter((r): r is PromotionRow => r != null)
    .filter((r) => (options?.includeInactive ? true : r.is_active === true));

  rows.sort((a, b) => {
    const pa = Number(a.priority ?? 0);
    const pb = Number(b.priority ?? 0);
    if (pb !== pa) return pb - pa;
    return String(b.updated_at ?? '').localeCompare(String(a.updated_at ?? ''));
  });

  const targetsByPromo = new Map<string, ReturnType<typeof mapTargetRow>[]>();
  for (const t of cache.promotionTargets ?? []) {
    const mapped = remoteTargetToRow(t);
    if (!mapped) continue;
    const list = targetsByPromo.get(mapped.promotion_id) ?? [];
    list.push(mapTargetRow(mapped));
    targetsByPromo.set(mapped.promotion_id, list);
  }

  return rows.map((row) => {
    const promotion = mapPromotionRow(row);
    return {
      ...promotion,
      targets: targetsByPromo.get(promotion.id) ?? [],
    };
  });
}

export const listPromotionsCache = async (options?: {
  includeInactive?: boolean;
}): Promise<PromotionWithTargets[]> => {
  const fromCache = listPromotionsFromWebCache(options);
  if (fromCache) return fromCache;

  // ADR-017 ramOnly: sin fallback SQLite.
  return [];
};

export const getPromotionByIdCache = async (promotionId: string): Promise<PromotionWithTargets | null> => {
  const cache = getCatalogRamSnapshotCache();
  if (cache?.promotions?.length) {
    const remote = cache.promotions.find((p) => p.id === promotionId);
    if (remote) {
      const orgId = String(remote.organization_id ?? '');
      const row = remotePromotionToRow(remote, orgId);
      if (row) {
        const promotion = mapPromotionRow(row);
        const targets = (cache.promotionTargets ?? [])
          .filter((t) => t.promotion_id === promotionId)
          .map(remoteTargetToRow)
          .filter((t): t is PromotionTargetRow => t != null)
          .map(mapTargetRow);
        return { ...promotion, targets };
      }
    }
  }
  return null;
};
